import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export interface TreeNode {
  name: string;
  children?: TreeNode[];
}

interface RichTreeViewProps {
  data: TreeNode[];
  onFileClick?: (path: string) => void;
  selectedPath?: string | null;
}

const fileIcon = (name: string) => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["json", "xml", "yaml", "yml"].includes(ext || ""))
    return <DataObjectIcon fontSize="small" sx={{ color: "#7c3aed" }} />;
  if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(ext || ""))
    return <ImageOutlinedIcon fontSize="small" sx={{ color: "#0891b2" }} />;
  if (["log", "txt", "html", "htm", "md", "out", "err"].includes(ext || ""))
    return <ArticleOutlinedIcon fontSize="small" sx={{ color: "#2563eb" }} />;
  return (
    <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: "#64748b" }} />
  );
};

const RichTreeView: React.FC<RichTreeViewProps> = ({
  data,
  onFileClick,
  selectedPath,
}) => {
  const [openNodes, setOpenNodes] = useState<{ [key: string]: boolean }>({});

  const handleToggle = (nodeId: string) =>
    setOpenNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));

  const renderNode = (node: TreeNode, nodeId: string, path: string, depth: number) => {
    const hasChildren = !!(node.children && node.children.length > 0);
    const isOpen = !!openNodes[nodeId];
    const isSelected = !hasChildren && selectedPath === path;

    return (
      <Box key={nodeId}>
        <Box
          onClick={() =>
            hasChildren ? handleToggle(nodeId) : onFileClick?.(path)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: `${depth * 18 + 8}px`,
            pr: 1,
            py: 0.6,
            borderRadius: 1.5,
            cursor: "pointer",
            userSelect: "none",
            color: isSelected ? "primary.main" : "text.primary",
            bgcolor: isSelected ? "primary.lighter" : "transparent",
            fontWeight: isSelected ? 700 : 500,
            "&:hover": { bgcolor: isSelected ? "primary.lighter" : "background.level2" },
          }}
        >
          <Box sx={{ width: 18, display: "flex", color: "text.secondary" }}>
            {hasChildren &&
              (isOpen ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              ))}
          </Box>
          {hasChildren ? (
            isOpen ? (
              <FolderOpenIcon fontSize="small" sx={{ color: "#E9672D" }} />
            ) : (
              <FolderIcon fontSize="small" sx={{ color: "#E9672D" }} />
            )
          ) : (
            fileIcon(node.name)
          )}
          <Typography sx={{ fontSize: "0.82rem", fontWeight: "inherit" }}>
            {node.name}
          </Typography>
        </Box>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {node.children!.map((child, index) =>
              renderNode(
                child,
                `${nodeId}-${index}`,
                path ? `${path}/${child.name}` : child.name,
                depth + 1
              )
            )}
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {data.map((node, index) => renderNode(node, `${index}`, node.name, 0))}
    </Box>
  );
};

export default RichTreeView;
